import { TextField, type TextFieldProps } from '@mui/material';
import { clamp } from 'lodash';
import { memo, useCallback, useEffect, useState } from 'react';

import type { ControlProps } from './SettingControl';

type T = ControlProps<number | string>;
export interface NumberControlProps
  extends T, Omit<TextFieldProps, keyof T | 'onChange'> {
  min?: number | string;
  max?: number | string;
  step?: number | string;
  type: 'number';
  onChange?: (value: number) => void;
}

export const NumberControl = memo<Omit<NumberControlProps, 'type'>>(
  ({
    name,
    label,
    description,
    value,
    min = 0,
    max = Infinity,
    step = 1,
    onChange,
    ...rest
  }) => {
    const [val, setVal] = useState<string>(() =>
      value === undefined || value === null ? String(min) : String(value),
    );

    // keep local input in sync when external value changes
    useEffect(() => {
      if (value !== undefined && value !== null && String(value) !== val) {
        // Sync local state with external controlled value
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setVal(String(value));
      }
    }, [val, value]);

    const commit = useCallback(
      (raw: string) => {
        // allow empty while typing, don't emit yet
        if (raw.trim() === '') {
          return;
        }

        const n = Number(raw);
        const lo = Number(min);
        const hi = Number(max);

        const next = Number.isNaN(n) ? lo : clamp(n, lo, hi);
        onChange?.(next);
      },
      [onChange, min, max],
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const raw = e.target.value;
        setVal(raw);
        // apply immediately on each keystroke
        commit(raw);
      },
      [commit],
    );

    const handleBlur = useCallback(() => {
      // if user leaves it empty, snap to min and emit
      if (val.trim() === '') {
        const lo = Number(min);
        setVal(String(lo));
        onChange?.(lo);
      }
    }, [val, min, onChange]);

    // HTML won't like "Infinity" as an attribute — omit it
    const htmlMax = Number.isFinite(Number(max)) ? max : undefined;

    return (
      <TextField
        fullWidth
        type="number"
        label={label || name}
        value={val}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{
          htmlInput: {
            min,
            max: htmlMax,
            step,
          },
        }}
        helperText={description}
        {...rest}
      />
    );
  },
);

NumberControl.displayName = 'NumberControl';
